import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, Empty, Tag } from "@/components/kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send, Plus, ShieldAlert, MessageSquare } from "lucide-react";

export default function Messages() {
  const { user } = useAuth();
  const [threads, setThreads] = useState(null);
  const [active, setActive] = useState(null);
  const [convo, setConvo] = useState(null);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const loadThreads = () => api.get("/threads").then((r) => setThreads(r.data.threads)).catch(() => setThreads(false));
  useEffect(() => { loadThreads(); }, []);

  const openThread = async (t) => {
    setActive(t);
    const { data } = await api.get(`/threads/${t.id}/messages`);
    setConvo(data);
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const send = async () => {
    if (!text.trim() || !active) return;
    const { data } = await api.post(`/threads/${active.id}/messages`, { text });
    if (data.redacted) toast.info("Contact details were hidden to protect privacy");
    setConvo((c) => ({ ...c, messages: [...c.messages, data.message] }));
    setText(""); loadThreads();
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  if (threads === null) return <Loader label="Loading messages" />;

  return (
    <div className="fade-up">
      <PageHeader overline="Secure messaging" title="Messages"
        subtitle="Coordinate without sharing personal phone numbers — contact details are automatically hidden."
        actions={<NewConversation role={user.role} onCreated={(t) => { loadThreads(); openThread(t); }} />} testid="messages-page" />

      <div className="grid lg:grid-cols-3 gap-4 h-[70vh]">
        <div className="krida-card p-2 overflow-y-auto">
          {threads.length === 0 ? <Empty label="No conversations" /> : threads.map((t) => (
            <button key={t.id} onClick={() => openThread(t)} data-testid={`thread-${t.id}`}
              className={`w-full text-left p-3 rounded-xl mb-1 transition-colors ${active?.id === t.id ? "bg-krida-crimson/15 border border-krida-crimson/30" : "hover:bg-white/5"}`}>
              <div className="font-semibold text-sm truncate">{t.counterpart_name}</div>
              <div className="text-xs text-slate-500 truncate">{t.last_message || t.subject}</div>
            </button>
          ))}
        </div>

        <div className="krida-card lg:col-span-2 flex flex-col">
          {!convo ? (
            <div className="flex-1 grid place-items-center text-slate-500"><div className="text-center"><MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" /><div className="font-mono text-sm">Select a conversation</div></div></div>
          ) : (
            <>
              <div className="p-4 border-b border-white/5 flex items-center gap-2">
                <div className="font-semibold">{active.counterpart_name}</div>
                <Tag tone="emerald"><ShieldAlert className="w-3 h-3" /> Private</Tag>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="convo-body">
                {convo.messages.map((m) => {
                  const mine = m.sender_id === convo.me;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-krida-crimson text-krida-bg" : "bg-white/8 text-slate-200"}`}>
                        {!mine && <div className="text-[10px] opacity-70 mb-0.5">{m.sender_name}</div>}
                        {m.text}
                        {m.redacted && <div className="text-[10px] mt-1 opacity-70">contact hidden</div>}
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <div className="p-3 border-t border-white/5 flex gap-2">
                <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…"
                  className="flex-1 px-4 py-2.5 rounded-full bg-black/40 border border-white/10 text-sm outline-none focus:border-krida-crimson/60" />
                <button onClick={send} aria-label="Send message" data-testid="message-send" className="w-11 h-11 rounded-full bg-krida-crimson hover:bg-krida-crimsonDark grid place-items-center text-krida-bg"><Send className="w-4 h-4" /></button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function NewConversation({ role, onCreated }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (!open) return;
    if (role === "organisation" || role === "admin") {
      api.get("/professionals", { params: { limit: 25 } }).then((r) =>
        setOptions(r.data.items.map((p) => ({ user_id: p.user_id, name: p.name, sub: `${p.primary_role} · ${p.location?.city}` }))));
    } else {
      api.get("/orgs").then((r) => setOptions(r.data.orgs.map((o) => ({ user_id: o.user_id, name: o.name, sub: o.org_type }))));
    }
  }, [open, role]);

  const start = async (o) => {
    const { data } = await api.post("/threads", { counterpart_user_id: o.user_id, counterpart_name: o.name });
    setOpen(false);
    onCreated({ id: data.thread_id, counterpart_name: o.name, subject: "" });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="new-conversation-btn" className="px-4 py-2.5 rounded-full bg-krida-crimson hover:bg-krida-crimsonDark text-krida-bg font-semibold text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> New message</button>
      </DialogTrigger>
      <DialogContent className="bg-krida-card border-white/10 text-slate-100">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Start a conversation</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {options.map((o) => (
            <button key={o.user_id} onClick={() => start(o)} data-testid={`compose-${o.user_id}`}
              className="w-full text-left p-3 rounded-xl hover:bg-white/5 transition-colors">
              <div className="font-semibold text-sm">{o.name}</div>
              <div className="text-xs text-slate-500">{o.sub}</div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
